import { RadioButtonWithLabelGroupProps, RadioButtonWithLabelGroupWidget } from '@tableau/widgets';
import { DivAttrs } from '@tableau/widgets/lib/src/utils/HtmlAttrs';
import { useField } from 'formik';
import * as React from 'react';

// 'name' is optional RadioButtonWithLabelGroupWidget but we need it for connecting with Formik field
export type FormikRadioButtonGroupProps<T> = Omit<RadioButtonWithLabelGroupProps<T>, 'handleChange'> & {name: string};

/**
 * A wrapper around {@link RadioButtonWithLabelGroupWidget} which auto-wires to a {@link Formik} field using the `name`
 * prop. The props are identical to {@link RadioButtonWithLabelGroupWidget}, except that the checked index and the change
 * handler are calculated automatically. It is still possible to supply them in props to override the default behavior.
 */
export function FormikRadioButtonGroup<Props>(props: FormikRadioButtonGroupProps<Props> & Omit<DivAttrs, 'role'>) {
  const [field, meta, helpers] = useField(props.name);

  const index = props.buttonList.findIndex(button => button.value === field.value);

  // we use initCheckedIndex ref variable to determine if the radio button group was 
  // touched or not. Note: if the user toggles between different radio button options and finally settles on the inititally 
  // checked option, the radio button group is considered untouched
  const initCheckedIndex = React.useRef(index);

  const changeHandler = React.useCallback((value: any, checkedIndex: number) => {
      helpers.setValue(value);
      helpers.setTouched(initCheckedIndex.current !== checkedIndex);
  }, [helpers.setValue, helpers.setTouched, initCheckedIndex]);

  return (
    <RadioButtonWithLabelGroupWidget buttonList={props.buttonList}
                                     checkedIndex={index}
                                     handleChange={changeHandler}
                                     {...props}
    />
  );
}
